import CollapsibleSection from 'components/ui/CollapsibleSection';
import QRCode from 'react-qr-code';
import Barcode from 'react-barcode';
import Flex from 'components/ui/Flex';
import styled, { createGlobalStyle } from 'styled-components';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SCANNER_CONFIGS } from './scannerActions';
import Text from 'components/ui/Text';
import Button from 'components/ui/Button';
import useDisplayFormat from 'hooks/useDisplayFormat';
import useAppSelector from 'hooks/useAppSelector';
import { configSelectors } from 'store/reducers/configSlice';

const PrintGlobalStyle = createGlobalStyle`
    @media print {
        body * {
            visibility: hidden !important;
        }
        .print-target,
        .print-target * {
            visibility: visible !important;
        }
        .print-target {
            position: fixed;
            left: 0;
            top: 0;
        }
    }
`;

const ScannerControl = () => {
    const { t } = useTranslation();
    const { format, setFormat } = useDisplayFormat();
    const reasonCodes = useAppSelector(configSelectors.selectReasonCodesMap);
    const [printingAction, setPrintingAction] = useState<string | null>(null);

    const handlePrint = (action: string) => {
        setPrintingAction(action);
        requestAnimationFrame(() => {
            window.print();
            setPrintingAction(null);
        });
    };

    const visibleConfigs = SCANNER_CONFIGS.filter((c) => c.showInControl !== false);
    const groupedConfigs = visibleConfigs.reduce((acc: Record<string, typeof SCANNER_CONFIGS>, config) => {
        const category = config.category || 'Other';
        if (!acc[category]) acc[category] = [];
        acc[category].push(config);
        return acc;
    }, {});

    const renderCode = (value: string, label?: string | null) => {
        if (format === 'qrcode') {
            return <QRCode value={value} size={140} />;
        }
        return (
            <BarcodeWrapper>
                <Barcode
                    value={value}
                    height={120}
                    fontSize={11}
                    margin={0}
                    // text={label}
                />
            </BarcodeWrapper>
        );
    };

    const renderReasonCodes =
        reasonCodes && Object.keys(reasonCodes).length > 0 ? (
            <CategorySection key="reasonCodes">
                <CategoryTitle xBold>{t('reasonCodes', 'Reason codes')}</CategoryTitle>
                <Flex gap="1rem" $wrap className="p-3">
                    {Object.values(reasonCodes).map((rc) => {
                        const payload = JSON.stringify({ action: 'bulkReason', value: rc.value });
                        const printKey = `bulkReason-${rc.label}`;
                        return (
                            <ScannerCard
                                key={printKey}
                                column
                                align="center"
                                gap="0.75rem"
                                className={printingAction === printKey ? 'print-target' : undefined}
                                $hidden={printingAction !== null && printingAction !== printKey}
                                $printVisible={printingAction === printKey}
                            >
                                {renderCode(payload, rc.label)}
                                <Text className="f3-600" fontSize={13}>
                                    {rc.label}
                                </Text>
                                <Text className="f3-400" fontSize={11} color="textFaded">
                                    {t('requiresDoubleConfirm', 'Requires double confirmation')}
                                </Text>
                                <NoPrint>
                                    <Button border rounded onClick={() => handlePrint(printKey)}>
                                        {t('print', 'Print')}
                                    </Button>
                                </NoPrint>
                            </ScannerCard>
                        );
                    })}
                </Flex>
            </CategorySection>
        ) : null;

    const renderActions = Object.entries(groupedConfigs).map(([cat, configs]) => {
        return (
            <CategorySection key={cat}>
                <CategoryTitle xBold>{t(`configCategories.${cat}`, cat)}</CategoryTitle>
                <Flex gap="1rem" $wrap className="p-3">
                    {configs.map((config) => {
                        return (
                            <ScannerCard
                                key={config.action}
                                column
                                align="center"
                                gap="0.75rem"
                                className={printingAction === config.action ? 'print-target' : undefined}
                                $hidden={printingAction !== null && printingAction !== config.action}
                                $printVisible={printingAction === config.action}
                            >
                                {renderCode(JSON.stringify(config.scannerValue), t(config.labelKey))}
                                <Text className="f3-600" fontSize={13}>
                                    {t(config.labelKey)}
                                </Text>
                                {config.requiresDoubleConfirm && (
                                    <Text className="f3-400" fontSize={11} color="textFaded">
                                        {t('requiresDoubleConfirm', 'Requires double confirmation')}
                                    </Text>
                                )}
                                <NoPrint>
                                    <Button border rounded onClick={() => handlePrint(config.action)}>
                                        {t('print', 'Print')}
                                    </Button>
                                </NoPrint>
                            </ScannerCard>
                        );
                    })}
                </Flex>
            </CategorySection>
        );
    });

    return (
        <Wrapper>
            {printingAction !== null && <PrintGlobalStyle />}
            <CollapsibleSection openLabel={t('scannerControl', 'Scanner Control')}>
                <FormatToggle align="center" gap="0.5rem" className="p-3">
                    <Text fontSize={13}>{t('displayFormat', 'Display format')}:</Text>
                    <ToggleGroup>
                        <ToggleBtn $active={format === 'barcode'} onClick={() => setFormat('barcode')}>
                            {t('barcode', 'Barcode')}
                        </ToggleBtn>
                        <ToggleBtn $active={format === 'qrcode'} onClick={() => setFormat('qrcode')}>
                            {t('qrCode', 'QR Code')}
                        </ToggleBtn>
                    </ToggleGroup>
                </FormatToggle>
                {renderActions}
                {renderReasonCodes}
            </CollapsibleSection>
        </Wrapper>
    );
};

export default ScannerControl;

const Wrapper = styled(Flex)`
    background-color: ${({ theme }) => theme.colors.fillWhite};
    border-radius: var(--radius);
`;

const CategorySection = styled.div`
    width: 100%;
    margin-top: 1rem;
`;

const CategoryTitle = styled(Text)`
    margin: 0.5rem 1rem;
`;

const ScannerCard = styled(Flex)<{ $hidden: boolean; $printVisible: boolean }>`
    padding: 1rem;
    border: 1px solid ${({ theme }) => theme.colors.borderLight};
    border-radius: var(--radius);
    ${({ $hidden }) => $hidden && 'visibility: hidden;'}

    @media print {
        display: ${({ $printVisible }) => ($printVisible ? 'flex' : 'none')};
        border: none;
        padding: 2rem;

        svg {
            width: 40vmin !important;
            height: 40vmin !important;
        }
    }
`;

const NoPrint = styled.div`
    margin-top: auto;
    @media print {
        display: none;
    }
`;

const BarcodeWrapper = styled.div`
    width: 250px;
    svg {
        width: 100% !important;
        height: auto !important;
    }
    @media print {
        width: 50vmin;
    }
`;

const FormatToggle = styled(Flex)`
    border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
    @media print {
        display: none;
    }
    font-family: inherit;
`;

const ToggleGroup = styled(Flex)`
    border: 1px solid ${({ theme }) => theme.colors.borderLight};
    border-radius: var(--radius);
    overflow: hidden;
`;

const ToggleBtn = styled.button<{ $active: boolean }>`
    padding: 0.25rem 0.75rem;
    font-size: 13px;
    background-color: ${({ theme, $active }) => ($active ? theme.colors.main : 'transparent')};
    color: ${({ theme, $active }) => ($active ? theme.colors.fillWhite : theme.colors.text)};
    border: none;
    cursor: pointer;
    transition: background 0.15s, color 0.15s;
    font-family: inherit;

    &:not(:last-child) {
        border-right: 1px solid ${({ theme }) => theme.colors.borderLight};
    }
`;
